import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components/macro';
import { Button, ButtonGroup } from '../../../../components/Buttons';
import { distances, grid } from '../../../../styles/constants';
import { useActions } from '../../../../Actions';
import { ApprovalsPaymentResponse } from '../../../../types/management-auth/generated';
import { Checkbox, Dropdown, Input, ValidationState } from '../../../../components/Forms';
import { formatDate, formatOrgNumber, formatTime } from '../../../../helpers/formatters';
import { FormikErrors, FormikTouched, useFormik } from 'formik';
import { validate, validateLength, validatePositiveNumber, validateRequired } from '../../../../helpers/validation';
import { getValueAt } from '../../../../helpers/getValueAt';
import { Label } from '../../../../components/Typography';
import BamboraAgreementTypeDropdown from './BamboraAgreementTypeDropdown';
import { ToggleDetails } from '../../connections/components/styledListItemComponents';
import { validateMD5 } from '../../../../helpers/validation/md5';
import { validateBamboraMerchantNumber } from '../../../../helpers/validation/bamboraMerchantNumber';

const fieldValidationMessage = (
    path: string,
    errors: FormikErrors<FormValues>,
    touched: FormikTouched<FormValues>,
    initialValues: FormValues
) => {
    const initialValue = getValueAt(path, initialValues);
    const error = getValueAt(path, errors);
    const was_touched = getValueAt(path, touched);
    if (error && (was_touched || initialValue)) {
        return {
            state: ValidationState.Invalid,
            message: error,
        };
    }
    return undefined;
};

const toOption = (approval: ApprovalsPaymentResponse | undefined, language: string) => {
    const country = approval?.bank_accounts?.[0]?.bank_account_country_code || '';
    const currency = approval?.bank_accounts?.[0]?.bank_account_currency;
    const org = approval?.organization_number || '';
    const date = formatDate(approval?.created_at || '', language);
    const time = formatTime(approval?.created_at || '', language);
    const mcc = approval?.mcc || '';
    return {
        label: [currency, date, time, country, formatOrgNumber(country, org).trim(), mcc].filter(x => x).join(', '),
        value: approval?.id || '',
    };
};

type AgreementTypes = 'direct' | 'payfac'

type FormValues = {
    agreement_type: AgreementTypes;
    case_id: string | undefined;
    merchant_number: string;
    md5_key: string;
    vipps: boolean;
    mcc: string;
};

type BamboraPayFacFormProps = {
    prodAccountId: string;
    activeApprovals: ApprovalsPaymentResponse[];
}

const BamboraForm = ({ prodAccountId, activeApprovals }: BamboraPayFacFormProps) => {
    const { t, i18n } = useTranslation();
    const { signupForConnection, closeModal } = useActions('connections.bambora');

    const formik = useFormik<FormValues>({
        initialValues: {
            agreement_type: 'payfac',
            case_id: activeApprovals[activeApprovals.length - 1]?.id,
            merchant_number: '',
            md5_key: '',
            vipps: false,
            mcc: '',
        },
        validate: (values) => {
            let errors: { [key: string]: string } = {};
            const validators = [
                {
                    path: 'agreement_type',
                    validator: validateRequired(t('settings.payment_connections.bambora_modal.invalid.agreement_type.required')),
                },
                {
                    path: 'case_id',
                    validator: validateRequired(t('settings.payment_connections.bambora_modal.invalid.case_id.required')),
                },
                {
                    path: 'merchant_number',
                    validator: validateRequired(t('settings.payment_connections.bambora_modal.invalid.merchant_number.required')),
                },
                {
                    path: 'merchant_number',
                    validator: validateBamboraMerchantNumber(t('settings.payment_connections.bambora_modal.invalid.merchant_number.invalid'), { ignoreEmpty: true }),
                },
                {
                    path: 'md5_key',
                    validator: validateRequired(t('settings.payment_connections.bambora_modal.invalid.md5_key.required')),
                },
                {
                    path: 'md5_key',
                    validator: validateMD5(t('settings.payment_connections.bambora_modal.invalid.md5_key.invalid'), { ignoreEmpty: true }),
                },
                {
                    path: 'mcc',
                    validator: validateLength(4, t('settings.payment_connections.bambora_modal.invalid.mcc.invalid')),
                },
                {
                    path: 'mcc',
                    validator: validatePositiveNumber(t('settings.payment_connections.bambora_modal.invalid.mcc.invalid')),
                },
            ];
            errors = validators.reduce((acc, elem) => {
                return validate(elem.path, elem.validator, values, acc);
            }, errors);

            return errors;
        },
        onSubmit: (values) => {
            const approvalBankAccounts = activeApprovals.find(a => a.id === values.case_id)?.bank_accounts || [];
            const currencies = [...(new Set<string>(approvalBankAccounts.map(x => x.bank_account_currency)))];
            if (prodAccountId && values.case_id && currencies.length > 0 && values.md5_key && values.merchant_number) {
                signupForConnection(prodAccountId, values.agreement_type, {
                    case_id: values.case_id,
                    merchant_number: values.merchant_number,
                    md5_key: values.md5_key,
                    payment_options: [
                        {
                            currencies,
                            type: 'bambora.creditcard',
                        },
                        ...(values.vipps ? [{
                            currencies: ['NOK'],
                            type: 'bambora.vipps' as const,
                        }] : []),
                    ],
                    ...(values.mcc?.length && values.agreement_type === 'payfac' && {
                        mcc: values.mcc,
                    }),
                });
            }
        },
    });

    return (
        <form onSubmit={formik.handleSubmit}>
            <SpacedWrapper>
                <InputWrapper>
                    <BamboraAgreementTypeDropdown
                        value={formik.values.agreement_type}
                        name= "agreement_type"
                        label= {t('settings.payment_connections.bambora_modal.fields.agreement_type')}
                        onChange={(value) => formik.setFieldValue('agreement_type', value)}
                        disabled={formik.isSubmitting}
                    />
                </InputWrapper>
            </SpacedWrapper>
            <SpacedWrapper>
                <InputWrapper>
                    <Dropdown
                        label={t('settings.payment_connections.bambora_modal.select_approval_label')}
                        name="approval"
                        value={toOption(activeApprovals.find(x => x.id === formik.values.case_id), i18n.language)}
                        onChange={option => {
                            formik.setFieldValue('case_id', option.value);
                        }}
                        disabled={activeApprovals.length === 0 || formik.isSubmitting}
                        options={
                            activeApprovals.reverse().map((a) => toOption(a, i18n.language))
                        }
                    />
                </InputWrapper>
                <InputWrapper>
                    <Input
                        label={t('settings.payment_connections.bambora_modal.fields.merchant_number')}
                        placeholder={t('settings.payment_connections.bambora_modal.placeholders.merchant_number')}
                        type="text"
                        name="merchant_number"
                        value={formik.values.merchant_number}
                        onChange={formik.handleChange}
                        onBlur={(e) => {
                            formik.handleChange(e);
                            formik.handleBlur(e);
                        }}
                        validation={fieldValidationMessage('merchant_number', formik.errors, formik.touched, formik.initialValues)}
                        required
                    />
                </InputWrapper>
                <InputWrapper>
                    <Input
                        label={t('settings.payment_connections.bambora_modal.fields.md5_key')}
                        placeholder={t('settings.payment_connections.bambora_modal.placeholders.md5_key')}
                        type="text"
                        name="md5_key"
                        value={formik.values.md5_key}
                        onChange={formik.handleChange}
                        onBlur={(e) => {
                            formik.handleChange(e);
                            formik.handleBlur(e);
                        }}
                        validation={fieldValidationMessage('md5_key', formik.errors, formik.touched, formik.initialValues)}
                        required
                    />
                </InputWrapper>
                <InputWrapper>
                    <Label>
                        {t('settings.payment_connections.bambora_callback_modal.fields.vipps')}
                    </Label>
                    <Checkbox
                        name="bambora-activate-vipps"
                        label={t(
                            'settings.payment_connections.bambora_callback_modal.fields.vipps'
                        )}
                        checked={formik.values.vipps}
                        onChange={() => {
                            formik.setFieldValue('vipps', !formik.values.vipps);
                        }}
                    />
                </InputWrapper>
            </SpacedWrapper>
            <SpacedWrapper>
                <OverrideMCCSettings>
                    <InputWrapper>
                        <Input
                            label={t('settings.payment_connections.bambora_modal.fields.mcc')}
                            placeholder={t('settings.payment_connections.bambora_modal.placeholders.mcc')}
                            type="text"
                            name="mcc"
                            value={formik.values.mcc}
                            onChange={formik.handleChange}
                            onBlur={(e) => {
                                formik.handleChange(e);
                                formik.handleBlur(e);
                            }}
                            validation={fieldValidationMessage('mcc', formik.errors, formik.touched, formik.initialValues)}
                        />
                    </InputWrapper>
                </OverrideMCCSettings>
            </SpacedWrapper>
            <ButtonGroup>
                <Button type="submit" disabled={formik.isSubmitting}>
                    {t(
                        'settings.payment_connections.bambora_modal.sign_up_with_bambora_call_to_action'
                    )}
                </Button>
                <Button className="alt" onClick={closeModal}>
                    {t('settings.payment_connections.bambora_modal.cancel')}
                </Button>
            </ButtonGroup>
        </form>
    );
};

const OverrideMCCSettings = ({
    children,
}: {
    children: React.ReactNode;
}) => {
    const [overrideSettings, setOverrideSettings] = useState(false);
    const { t } = useTranslation();
    return (<>
        <InputWrapper>
            <ToggleDetails type="button" onClick={() => setOverrideSettings(!overrideSettings)}>
                {t('settings.payment_connections.bambora_modal.advanced_toggle', {
                    context: overrideSettings ? 'hide' : 'show',
                })}
            </ToggleDetails>
        </InputWrapper>
        {overrideSettings && <>
            {children}
        </>}
    </>
    );
};


const SpacedWrapper = styled.div`
    margin-top: ${distances.small};
    margin-bottom: ${distances.normal};
`;

const InputWrapper = styled.div`
    margin-right: ${grid.gutter};

    &:last-child {
        margin: 0;
    }
`;

export default BamboraForm;
